/**
 * Skip rules for visit-recording capture.
 *
 * Components that don't snapshot reliably (camera, signature pad, map, WebView)
 * or don't need pixel capture (Alert.alert dialogs) call SkipRules.notify()
 * on mount and SkipRules.clear() on unmount. The native module subscribes via
 * onChange() to pause/resume the encoder at scene boundaries. The event logger
 * emits capture_paused / capture_resumed events so the player can label gaps.
 */
import type { SkipReason } from "./types";

type Listener = (active: SkipReason[]) => void;

const activeReasons = new Set<SkipReason>();
const listeners = new Set<Listener>();

function emit(): void {
    const snapshot = Array.from(activeReasons);
    for (const l of listeners) {
        try {
            l(snapshot);
        } catch {
            /* listener throws are isolated */
        }
    }
}

export const SkipRules = {
    notify(reason: SkipReason): void {
        if (activeReasons.has(reason)) {
            return;
        }
        activeReasons.add(reason);
        emit();
    },

    clear(reason: SkipReason): void {
        if (!activeReasons.has(reason)) {
            return;
        }
        activeReasons.delete(reason);
        emit();
    },

    isActive(): boolean {
        return activeReasons.size > 0;
    },

    activeReasons(): SkipReason[] {
        return Array.from(activeReasons);
    },

    onChange(listener: Listener): () => void {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },

    /** Test-only reset. */
    _reset(): void {
        activeReasons.clear();
        listeners.clear();
    }
};
